import { TrashIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { ChevronToggle } from 'app/components/ChevronToggle'
import FloatingSave from 'app/components/FloatingSave'
import FormCombo from 'app/components/form/FormCombo'
import FormComboMultiSelect from 'app/components/form/FormComboMultiSelect'
import FormDatePicker from 'app/components/form/FormDatePicker'
import FormPanel from 'app/components/form/FormPanel'
import { SmartForm } from 'app/components/form/SmartForm'
import ChipsButton from 'app/components/shared/ChipsButton'
import { Tooltip } from 'app/components/Tooltip'
import {
  brokenAppointmentCodeOptions,
  completedAppointmentStatusOptions,
  enabledDisabledOptions,
  primaryRuleOptions,
  ANY_ADA_CODE,
} from 'app/components/pms-config/usePmsConfig'
import type { UsePmsConfigReturn } from 'app/components/pms-config/usePmsConfig'
import { OptionType } from 'types/OptionType'

const inputClassName = `rounded-lg border border-default bg-surface px-4 py-3 text-body-sm text-subtle shadow-elevation-1`
const labelClassName = `text-label-sm`

function DeletableOption({ name, selected, onDelete }: { name: string; selected: boolean; onDelete: () => void }) {
  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-x-2">
        <input
          type="checkbox"
          checked={selected}
          readOnly
          className="h-4 w-4 rounded border-(--control-border) text-default outline-none focus:outline-none focus:ring-0 focus:ring-offset-0"
        />
        <span>{name}</span>
      </div>
      <Tooltip message="Delete custom code" className="!cursor-pointer">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onDelete()
          }}
          className="p-1 text-minimal hover:text-subtle"
        >
          <TrashIcon className="h-4 w-4 text-default" />
        </button>
      </Tooltip>
    </div>
  )
}

export type PmsConfigComponentProps = UsePmsConfigReturn & {
  validator: any
}

export function PmsConfigComponent(props: PmsConfigComponentProps) {
  const {
    location,
    isDirtyForm,
    formRef,
    selectedPrimaryRules,
    hasAllProceduresSelected,
    hasAdaSelected,
    showEligibleProceduresDropdown,
    handlePrimaryRuleChange,
    eligibleProcedureOpts,
    selectedEligibleProcedures,
    hasAnyAdaCodeSelected,
    handleEligibleProcedureChange,
    selectedPediatric,
    setSelectedPediatric,
    brokenCodeOpts,
    selectedBrokenCodes,
    setSelectedBrokenCodes,
    completionStatusOpts,
    selectedCompletionStatuses,
    setSelectedCompletionStatuses,
    selectedMigrationHandling,
    setSelectedMigrationHandling,
    hiddenInputs,
    handleAddEligibleProcedure,
    handleDeleteEligibleProcedure,
    handleAddBrokenCode,
    handleDeleteBrokenCode,
    handleAddCompletionStatus,
    handleDeleteCompletionStatus,
    setIsDirtyForm,
    handleSaveConfiguration,
    validator,
  } = props

  const eligibleProcedureOptsWithDelete = useMemo(
    () =>
      eligibleProcedureOpts.map((opt) =>
        opt.meta?.isDeletable
          ? {
              ...opt,
              meta: {
                ...opt.meta,
                customComboOption: ({ selected }: { selected: boolean }) => (
                  <DeletableOption
                    name={opt.name}
                    selected={selected}
                    onDelete={() => handleDeleteEligibleProcedure(opt)}
                  />
                ),
              },
            }
          : opt,
      ),
    [eligibleProcedureOpts, handleDeleteEligibleProcedure],
  )

  const brokenCodeOptsWithDelete = useMemo(
    () =>
      brokenCodeOpts.map((opt) =>
        opt.meta?.isDeletable
          ? {
              ...opt,
              meta: {
                ...opt.meta,
                customComboOption: ({ selected }: { selected: boolean }) => (
                  <DeletableOption name={opt.name} selected={selected} onDelete={() => handleDeleteBrokenCode(opt)} />
                ),
              },
            }
          : opt,
      ),
    [brokenCodeOpts, handleDeleteBrokenCode],
  )

  const completionStatusOptsWithDelete = useMemo(
    () =>
      completionStatusOpts.map((opt) =>
        opt.meta?.isDeletable
          ? {
              ...opt,
              meta: {
                ...opt.meta,
                customComboOption: ({ selected }: { selected: boolean }) => (
                  <DeletableOption
                    name={opt.name}
                    selected={selected}
                    onDelete={() => handleDeleteCompletionStatus(opt)}
                  />
                ),
              },
            }
          : opt,
      ),
    [completionStatusOpts, handleDeleteCompletionStatus],
  )

  return (
    <SmartForm
      id="pms-config-settings"
      className="mt-0!"
      replace
      validator={validator}
      method="post"
      hideButtons={true}
      hiddenInputs={hiddenInputs}
      formRef={formRef}
    >
      <FormPanel title="New Patient Calculation" cols={6}>
        <div className="flex flex-col gap-y-6">
          {selectedPrimaryRules.map((opt) => (
            <input key={opt.value} type="hidden" name="newPatientCalculation" value={opt.value} />
          ))}
          <FormComboMultiSelect
            name="_newPatientCalculation"
            label="Primary Rule"
            cols={6}
            labelClassName={labelClassName}
            inputClassName={inputClassName}
            placeholder="Select rules"
            options={primaryRuleOptions}
            values={selectedPrimaryRules}
            checkboxSide="left"
            customButton={(isOpen) => (
              <div
                role="button"
                tabIndex={0}
                data-testid="primary-rule-button"
                className="flex min-h-[42px] w-full items-center justify-between rounded-lg border border-default bg-surface px-3 py-2 shadow-elevation-1"
              >
                <span data-testid="primary-rule-value" className="text-body-sm text-default">
                  {selectedPrimaryRules.length > 0 ? (
                    hasAllProceduresSelected ? (
                      `Any Procedure Transaction`
                    ) : (
                      selectedPrimaryRules.map((r) => r.name).join(`, `)
                    )
                  ) : (
                    <span className="text-minimal">Select rules</span>
                  )}
                </span>
                <ChevronToggle className="pr-0" open={isOpen} />
              </div>
            )}
            onChange={handlePrimaryRuleChange}
          />
          {showEligibleProceduresDropdown && (
            <div data-testid="eligible-procedures-section">
              {selectedEligibleProcedures.map((opt) => (
                <input key={opt.value} type="hidden" name="eligibleProcedureTransactions" value={opt.value} />
              ))}
              <FormComboMultiSelect
                name="_eligibleProcedureTransactions"
                label={
                  hasAdaSelected ? `Additional Eligible Procedure Transactions` : `Eligible Procedure Transactions`
                }
                cols={6}
                labelClassName={labelClassName}
                inputClassName={inputClassName}
                placeholder="Select codes"
                options={eligibleProcedureOptsWithDelete}
                values={selectedEligibleProcedures}
                addText="Add New Dental Code"
                checkboxSide="left"
                customButton={
                  <div data-testid="eligible-procedures-button">
                    <ChipsButton options={selectedEligibleProcedures} placeholder="Select codes" />
                  </div>
                }
                onChange={handleEligibleProcedureChange}
                onAddNewValue={handleAddEligibleProcedure}
              />
            </div>
          )}
          <FormCombo
            name="excludeParentsFromNewPatients"
            label="Pediatric - Remove Adults as New Patients"
            cols={6}
            labelClassName={labelClassName}
            inputClassName={inputClassName}
            options={enabledDisabledOptions}
            value={selectedPediatric}
            onChange={(option) => {
              setSelectedPediatric(option)
              setIsDirtyForm(true)
            }}
          />
        </div>
      </FormPanel>

      <FormPanel title="Appointments" cols={6}>
        <div className="flex flex-col gap-y-6">
          {selectedBrokenCodes.map((opt) => (
            <input key={opt.value} type="hidden" name="transactionBrokenCodes" value={opt.value} />
          ))}
          <FormComboMultiSelect
            name="_transactionBrokenCodes"
            label="Broken Appointment Codes"
            cols={6}
            labelClassName={labelClassName}
            inputClassName={inputClassName}
            placeholder="Select codes"
            options={brokenCodeOptsWithDelete}
            values={selectedBrokenCodes}
            addText="Add custom code"
            checkboxSide="left"
            customButton={
              <div data-testid="broken-codes-button">
                <ChipsButton options={selectedBrokenCodes} placeholder="Select codes" />
              </div>
            }
            onChange={(options) => {
              const defaultBrokenCodes = brokenAppointmentCodeOptions.filter((o) => o.meta?.isDefault)
              const withDefaults = [...defaultBrokenCodes, ...options.filter((o: OptionType) => !o.meta?.isDefault)]
              setSelectedBrokenCodes(withDefaults)
              setIsDirtyForm(true)
            }}
            onAddNewValue={handleAddBrokenCode}
          />
          {selectedCompletionStatuses.map((opt) => (
            <input key={opt.value} type="hidden" name="appointmentCompletionStatuses" value={opt.value} />
          ))}
          <FormComboMultiSelect
            name="_appointmentCompletionStatuses"
            label="Completed Appointment Statuses"
            cols={6}
            labelClassName={labelClassName}
            inputClassName={inputClassName}
            placeholder="Select statuses"
            options={completionStatusOptsWithDelete}
            values={selectedCompletionStatuses}
            addText="Add custom status"
            checkboxSide="left"
            customButton={
              <div data-testid="completion-status-button">
                <ChipsButton options={selectedCompletionStatuses} placeholder="Select statuses" />
              </div>
            }
            onChange={(options) => {
              const defaultStatuses = completedAppointmentStatusOptions.filter((o) => o.meta?.isDefault)
              const withDefaults = [...defaultStatuses, ...options.filter((o: OptionType) => !o.meta?.isDefault)]
              setSelectedCompletionStatuses(withDefaults)
              setIsDirtyForm(true)
            }}
            onAddNewValue={handleAddCompletionStatus}
          />
        </div>
      </FormPanel>

      <FormPanel title="Migration Handling" cols={6}>
        <div className="flex flex-col gap-y-6">
          <FormCombo
            name="removeNewPatientBeforeMigration"
            label="Remove New Patient if First Activity on the Patient is Before the Migration Date"
            cols={6}
            labelClassName={labelClassName}
            inputClassName={inputClassName}
            options={enabledDisabledOptions}
            value={selectedMigrationHandling}
            onChange={(option) => {
              setSelectedMigrationHandling(option)
              setIsDirtyForm(true)
            }}
          />
          <FormDatePicker
            name="migrationDate"
            label="Date of Migration"
            cols={6}
            labelClassName={labelClassName}
            className={inputClassName}
            placeholder="mm/dd/yyyy"
            disabled={selectedMigrationHandling?.value === `false`}
            value={location?.migrationDate?.toString()}
            onChange={() => setIsDirtyForm(true)}
          />
        </div>
      </FormPanel>

      <div className="pb-11" />
      <FloatingSave isDirtyForm={isDirtyForm} handleSaveConfiguration={handleSaveConfiguration} />
    </SmartForm>
  )
}
